import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft, MapPin, HardDrive, Receipt, Package as PackageIcon, RefreshCw,
  DollarSign, Gift, Ban, Play, X, Check, AlertTriangle, CreditCard, Cpu,
} from 'lucide-react';
import { api } from '../../services/apiService';
import { getTenantCreditsSummary } from '../../services/aiUsageApi';
import { RubyLoader } from '../RubyLoader';

import { RubyGemMark } from '../RubyGemMark';
/**
 * FINANCE HUB — TENANT DETAIL (in-page).
 *
 * Everything about one tenant's money, and everything you can DO about it,
 * without leaving the hub. Actions here call the same endpoints the old
 * per-tenant screens used, so changes land in the right place.
 */

interface Props {
  tenant: any;
  locations: any[];
  stripePrices: any[];
  skus: any[];
  onBack: () => void;
  onChanged: () => void;   // parent refetch after a mutation
}

const money = (c?: number) =>
  `$${((c || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dateOf = (ms?: number | string) => {
  if (!ms) return '—';
  const d = typeof ms === 'string' ? new Date(ms) : new Date(ms);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

type Busy = { id: string; action: string } | null;

const TenantFinanceDetail: React.FC<Props> = ({ tenant, locations, stripePrices, skus, onBack, onChanged }) => {
  const [events, setEvents] = useState<any[]>([]);
  const [ents, setEnts] = useState<any | null>(null);
  const [credits, setCredits] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<Busy>(null);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [priceFor, setPriceFor] = useState<any | null>(null);   // location being repriced
  const [chosenPrice, setChosenPrice] = useState('');

  const tenantId = tenant.id;

  const load = async () => {
    setLoading(true);
    const [b, e, c] = await Promise.allSettled([
      api.superAdmin.getTenantBilling(tenantId),
      api.billingCatalog.getEntitlements(tenantId),
      getTenantCreditsSummary(tenantId, { preset: '30d' } as any),
    ]);
    setEvents(b.status === 'fulfilled' ? ((b.value as any)?.events ?? []) : []);
    setEnts(e.status === 'fulfilled' ? e.value : null);
    setCredits(c.status === 'fulfilled' ? c.value : null);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);

  const myLocations = useMemo(
    () => locations.filter(l => l.tenantId === tenantId),
    [locations, tenantId],
  );
  const activeLocations = myLocations.filter(l => (l.status || 'active') !== 'inactive');
  const locationTotal = activeLocations.reduce(
    (s, l) => s + (l.isFree ? 0 : (l.priceAmount || l.monthlyAmount || 0)), 0);

  const skuById = useMemo(() => Object.fromEntries(skus.map(s => [s.skuId, s])), [skus]);
  const entitled: any[] = ents?.entitlements ?? [];
  const enabledSkus = entitled.map(e => skuById[e.skuId]).filter(Boolean);
  const deviceSkus = skus.filter(s => s.type === 'device_finance');
  const financedDevices = enabledSkus.filter(s => s.type === 'device_finance');
  const financeMonthly = financedDevices.reduce((s, d) => s + (d.amountCents || 0), 0);
  const disabledSkus = skus.filter(s => s.type === 'package' && !enabledSkus.some(e => e.skuId === s.skuId));

  const nextStripeAmount = (tenant.subscriptionCost || 0) + locationTotal + financeMonthly;

  const run = async (id: string, action: string, fn: () => Promise<any>, okText: string) => {
    try {
      setBusy({ id, action }); setMsg(null);
      await fn();
      setMsg({ kind: 'ok', text: okText });
      onChanged();
      await load();
    } catch (e: any) {
      setMsg({ kind: 'err', text: e?.message || `${action} failed` });
    } finally {
      setBusy(null);
      setTimeout(() => setMsg(null), 6000);
    }
  };

  return (
    <div className="space-y-5 text-gray-900">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <button onClick={onBack}
                  className="mt-0.5 w-9 h-9 rounded-lg border border-gray-300 bg-white inline-flex items-center justify-center hover:bg-gray-50 min-h-[40px]">
            <ArrowLeft size={16} />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{tenant.name || tenant.businessName || tenantId}</h2>
            <p className="text-xs text-gray-500">
              {tenantId} · {tenant.ownerEmail || '—'} · {tenant.productType || 'no product'}
            </p>
          </div>
        </div>
        <button onClick={load} disabled={loading}
                className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm inline-flex items-center gap-2 min-h-[40px]">
          {loading ? <RubyGemMark size={14} drawing color="currentColor" /> : <RefreshCw size={14} />} Refresh
        </button>
      </div>

      {msg && (
        <div className={`rounded-xl px-4 py-2.5 text-sm flex items-center gap-2 border ${
          msg.kind === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                            : 'bg-red-50 border-red-200 text-red-900'}`}>
          {msg.kind === 'ok' ? <Check size={15} /> : <AlertTriangle size={15} />} {msg.text}
        </div>
      )}

      {/* Money summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Next Stripe charge" value={money(nextStripeAmount)}
             sub={tenant.currentPeriodEnd ? `on ${dateOf(tenant.currentPeriodEnd)}` : 'no renewal date'} accent />
        <Kpi label="Subscription" value={money(tenant.subscriptionCost)} sub={tenant.priceId || 'no price'} />
        <Kpi label="Locations" value={money(locationTotal)} sub={`${activeLocations.length} active of ${myLocations.length}`} />
        <Kpi label="Device financing" value={money(financeMonthly)}
             sub={`${financedDevices.length} of ${deviceSkus.length} device SKUs`} />
      </div>

      {/* LOCATIONS — with inline controls */}
      <Card title="Locations" icon={<MapPin size={15} />} right={`${activeLocations.length} active`}>
        {myLocations.length === 0 ? (
          (loading ? <RubyLoader variant="section" label="Loading…" /> : <Empty text="No locations on this tenant." />)
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left">Location</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-right">Monthly</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {myLocations.map(l => {
                const inactive = (l.status || 'active') === 'inactive';
                const b = busy?.id === l.requestId;
                return (
                  <tr key={l.requestId} className={inactive ? 'opacity-60' : ''}>
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-gray-900">{l.name || l.requestId}</div>
                      <div className="text-[11px] text-gray-400">{l.requestId}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        inactive ? 'bg-gray-200 text-gray-600'
                        : l.status === 'grace' ? 'bg-amber-100 text-amber-700'
                        : l.status === 'pending' ? 'bg-sky-100 text-sky-700'
                        : 'bg-emerald-100 text-emerald-700'}`}>{l.status || 'active'}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {l.isFree ? <span className="text-emerald-600 font-medium">Free</span>
                                : money(l.priceAmount || l.monthlyAmount || 0)}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        <Act label="Change price" icon={<DollarSign size={12} />} disabled={b}
                             onClick={() => { setPriceFor(l); setChosenPrice(l.priceId || ''); }} />
                        {!l.isFree && (
                          <Act label="Make free" icon={<Gift size={12} />} disabled={b}
                               onClick={() => run(l.requestId, 'make free',
                                 () => api.locationBilling.makeFreeRequest(l.requestId, { tenantId }),
                                 `${l.name || 'Location'} is now free`)} />
                        )}
                        {inactive ? (
                          <Act label="Reactivate" icon={<Play size={12} />} disabled={b} tone="good"
                               onClick={() => run(l.requestId, 'reactivate',
                                 () => api.locationBilling.reactivateRequest(l.requestId, { tenantId }),
                                 `${l.name || 'Location'} reactivated`)} />
                        ) : (
                          <Act label="Deactivate" icon={<Ban size={12} />} disabled={b} tone="bad"
                               onClick={() => run(l.requestId, 'deactivate',
                                 () => api.locationBilling.deactivateRequest(l.requestId, { tenantId }),
                                 `${l.name || 'Location'} deactivated`)} />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-900 text-white">
              <tr>
                <td className="px-4 py-2.5 font-semibold" colSpan={2}>Locations total</td>
                <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{money(locationTotal)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        )}
      </Card>

      {/* DEVICES */}
      <Card title="Machines & financing" icon={<HardDrive size={15} />}
            right={`${financedDevices.length} financed · ${money(financeMonthly)}/mo`}>
        {deviceSkus.length === 0 ? (
          <Empty text="No device SKUs in the catalog." />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[11px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left">Device</th>
                <th className="px-4 py-2 text-left">Term</th>
                <th className="px-4 py-2 text-left">Type</th>
                <th className="px-4 py-2 text-right">Monthly</th>
                <th className="px-4 py-2 text-left">On this tenant</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {deviceSkus.map(d => {
                const on = financedDevices.some(f => f.skuId === d.skuId);
                return (
                  <tr key={d.skuId} className={on ? '' : 'opacity-60'}>
                    <td className="px-4 py-2.5 font-medium text-gray-900">{d.name}</td>
                    <td className="px-4 py-2.5 text-gray-600">{d.termMonths ? `${d.termMonths} months` : '—'}</td>
                    <td className="px-4 py-2.5 text-gray-600">{d.financeType || '—'}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">
                      {d.amountCents ? money(d.amountCents) : <span className="text-emerald-600">Free</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      {on ? <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs">financed</span>
                          : <span className="text-xs text-gray-400">not assigned</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <p className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-[11px] text-gray-500">
          Devices are sold through a deal. A device arrear escalates to collections — it never disables the till.
        </p>
      </Card>

      {/* PACKAGES: enabled vs disabled */}
      <Card title="Packages" icon={<PackageIcon size={15} />}
            right={`${enabledSkus.filter(s => s.type === 'package').length} enabled · ${disabledSkus.length} disabled`}>
        <div className="p-4 space-y-3">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-1.5">Enabled</p>
            <div className="flex flex-wrap gap-1.5">
              {enabledSkus.filter(s => s.type === 'package').map(s => (
                <span key={s.skuId} className="px-2 py-1 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-800">
                  {s.name} {s.amountCents ? <span className="tabular-nums">· {money(s.amountCents)}</span>
                                          : <span className="text-emerald-600">· not priced</span>}
                </span>
              ))}
              {enabledSkus.filter(s => s.type === 'package').length === 0 && (
                <span className="text-xs text-gray-400">none</span>
              )}
            </div>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-1.5">Disabled</p>
            <div className="flex flex-wrap gap-1.5">
              {disabledSkus.map(s => (
                <span key={s.skuId} className="px-2 py-1 rounded-lg bg-gray-100 border border-gray-200 text-xs text-gray-500">
                  {s.name}
                </span>
              ))}
              {disabledSkus.length === 0 && <span className="text-xs text-gray-400">none</span>}
            </div>
          </div>
        </div>
      </Card>

      {/* TOKENS + BILLING HISTORY */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <Card title="Ruby Tokens" icon={<Cpu size={15} />} right="last 30 days">
          {credits ? (
            <div className="p-4 space-y-2 text-sm">
              <Row label="Balance" value={(credits.balance ?? 0).toLocaleString()} strong />
              <Row label="Consumed (30d)" value={(credits.totals?.credits ?? 0).toLocaleString()} />
              <Row label="AI calls" value={(credits.totals?.calls ?? 0).toLocaleString()} />
              <Row label="Cost → charged"
                   value={`$${(credits.totals?.realCostUsd ?? 0).toFixed(2)} → $${(credits.totals?.revenueUsd ?? 0).toFixed(2)}`} />
            </div>
          ) : <Empty text="No token ledger data." />}
        </Card>

        <Card title="Billing history" icon={<Receipt size={15} />} right={`${events.length} events`}>
          {events.length === 0 ? (loading ? <RubyLoader variant="section" label="Loading…" /> : <Empty text="No billing events." />) : (
            <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
              {events.slice(0, 30).map((ev, i) => (
                <div key={`${ev.eventTimestamp}-${i}`} className="px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 truncate">{ev.type}</p>
                    <p className="text-[11px] text-gray-500">{dateOf(ev.eventTimestamp)}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {typeof ev.amountPaid === 'number' && (
                      <span className="tabular-nums">{money(ev.amountPaid)}</span>
                    )}
                    {ev.invoiceUrl && (
                      <a href={ev.invoiceUrl} target="_blank" rel="noreferrer"
                         className="text-xs text-[#DC143C] min-h-[36px] inline-flex items-center">view</a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Change-price modal (no native dialogs) */}
      {priceFor && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setPriceFor(null)}>
          <div className="bg-white text-gray-900 rounded-2xl w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="font-semibold">Change location price</h3>
                <p className="text-[11px] text-gray-400">{priceFor.name || priceFor.requestId}</p>
              </div>
              <button onClick={() => setPriceFor(null)} className="text-gray-400 hover:text-gray-700 px-2 min-h-[44px]"><X size={18} /></button>
            </div>
            <div className="p-5">
              <label className="block text-xs font-medium text-gray-600 mb-1">Stripe price</label>
              <select value={chosenPrice} onChange={e => setChosenPrice(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[44px]">
                <option value="">— choose a price —</option>
                {stripePrices.map((p: any) => (
                  <option key={p.id} value={p.id}>
                    {p.nickname || p.productName || p.id} — ${((p.unitAmount ?? 0) / 100).toFixed(2)}/{p.interval}
                  </option>
                ))}
              </select>
              <p className="text-[11px] text-gray-400 mt-2">
                Applies to this location's own subscription. The tenant's plan is unchanged.
              </p>
            </div>
            <div className="px-5 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button onClick={() => setPriceFor(null)}
                      className="px-4 py-2 rounded-lg border border-gray-300 text-sm min-h-[44px]">Cancel</button>
              <button
                disabled={!chosenPrice}
                onClick={() => {
                  const loc = priceFor; setPriceFor(null);
                  run(loc.requestId, 'change price',
                    () => api.locationBilling.changePriceRequest(loc.requestId, { tenantId, priceId: chosenPrice }),
                    `${loc.name || 'Location'} price updated`);
                }}
                className="px-4 py-2 rounded-lg bg-[#DC143C] text-white text-sm font-medium min-h-[44px] disabled:opacity-50"
              >
                Apply price
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Kpi: React.FC<{ label: string; value: string; sub?: string; accent?: boolean }> = ({ label, value, sub, accent }) => (
  <div className={`rounded-xl border p-4 ${accent ? 'bg-gray-900 text-white border-gray-900' : 'bg-white border-gray-200'}`}>
    <div className={`text-[11px] uppercase tracking-wide ${accent ? 'text-gray-400' : 'text-gray-500'}`}>{label}</div>
    <div className="text-2xl font-semibold tabular-nums mt-1">{value}</div>
    {sub && <div className={`text-xs mt-0.5 ${accent ? 'text-gray-400' : 'text-gray-500'}`}>{sub}</div>}
  </div>
);

const Card: React.FC<{ title: string; icon: React.ReactNode; right?: string; children: React.ReactNode }> =
  ({ title, icon, right, children }) => (
  <div className="bg-white text-gray-900 rounded-xl border border-gray-200 overflow-hidden">
    <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-2">
      <h3 className="font-semibold text-sm flex items-center gap-2"><span className="text-gray-500">{icon}</span>{title}</h3>
      {right && <span className="text-xs text-gray-500">{right}</span>}
    </div>
    {children}
  </div>
);

const Act: React.FC<{ label: string; icon: React.ReactNode; onClick: () => void; disabled?: boolean; tone?: 'good' | 'bad' }> =
  ({ label, icon, onClick, disabled, tone }) => (
  <button onClick={onClick} disabled={disabled}
    className={`px-2.5 py-1.5 rounded-lg border text-xs inline-flex items-center gap-1 min-h-[36px] disabled:opacity-40 ${
      tone === 'bad' ? 'border-red-200 text-red-700 hover:bg-red-50'
      : tone === 'good' ? 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
      : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
    {icon}{label}
  </button>
);

const Row: React.FC<{ label: string; value: string; strong?: boolean }> = ({ label, value, strong }) => (
  <div className="flex justify-between">
    <span className="text-gray-500">{label}</span>
    <span className={`tabular-nums ${strong ? 'font-semibold' : ''}`}>{value}</span>
  </div>
);

const Empty: React.FC<{ text: string }> = ({ text }) => (
  <p className="px-4 py-6 text-center text-sm text-gray-400">{text}</p>
);

export default TenantFinanceDetail;
